import React, { useEffect, useState, useCallback, useRef } from 'react';
import bowser from 'yola-bowser';
import Box from '../../box';
import Icon from '../../icon';
import Slider from '../../slider';
import Paragraph from '../../paragraph';
import ValueInput from '../../value-input';
import parseValue from '../../value-input/helpers/parse-value';
import IconButton from '../../icon-button';
import HorizontalStack from '../../horizontal-stack';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover';
import Heading from '../../heading';
import ValuePopover from './value-popover';
const getMin = (min, value) => {
  if (typeof min === 'function') {
    return min(value);
  }

  return min;
};

const getMax = (max, value) => {
  if (typeof max === 'function') {
    return max(value);
  }

  return max;
};

const getStep = (step, value) => {
  if (typeof step === 'function') {
    return step(value);
  }

  return step;
};

const roundValueFormatter = (v) => Math.round(v);

const defaultSupportedUnits = [];

const BasicSliderPattern = ({
  iconGlyph,
  min,
  max,
  startPoint,
  value,
  selected = false,
  indeterminate = false,
  isValueEditable = true,
  mixedLabel,
  title,
  step,
  supportedUnits = defaultSupportedUnits,
  popoverProps = undefined,
  valuePopoverProps = undefined,
  onChange = Function.prototype,
  onAfterChange = Function.prototype,
  onClick = undefined,
}) => {
  const elementRef = useRef(null);
  const [isValuePopoverVisible, setIsValuePopoverVisible] = useState(false);

  const isTouchDevice = bowser.mobile || bowser.tablet;
  const popoverMode = isTouchDevice ? 'popover' : 'tooltip';
  const currentValue = `${value}`;
  const { value: parsedValue, unit: parsedUnit } = parseValue(currentValue, supportedUnits);
  const inputValue = indeterminate ? mixedLabel : currentValue;
  const isValuePopoverEnabled = !!valuePopoverProps;
  const hasValuePopoverChecker =
    isValuePopoverEnabled && typeof valuePopoverProps?.checkVisibility === 'function';

  const showValuePopover = useCallback(() => {
    setIsValuePopoverVisible(true);
  }, []);

  const hideValuePopover = useCallback(() => {
    setIsValuePopoverVisible(false);
  }, []);

  const toggleValuePopover = useCallback(
    (val) => {
      const checkVisibility = valuePopoverProps?.checkVisibility;

      if (hasValuePopoverChecker && checkVisibility) {
        const { value: newValue } = parseValue(val.toString(), supportedUnits);
        const valuePopovervisibility = checkVisibility(Number(newValue));

        if (valuePopovervisibility) {
          showValuePopover();
        } else {
          hideValuePopover();
        }
      }
    },
    [hasValuePopoverChecker, supportedUnits, valuePopoverProps, showValuePopover, hideValuePopover]
  );

  const handleSliderChange = useCallback(
    (val) => {
      const newValue = parsedUnit ? `${val}${parsedUnit}` : val;

      onChange(newValue);
      toggleValuePopover(newValue);
    },
    [parsedUnit, toggleValuePopover, onChange]
  );

  const handleValueInputBlur = useCallback(
    (val) => {
      onAfterChange(val);

      if (isValuePopoverEnabled) {
        hideValuePopover();
      }
    },
    [isValuePopoverEnabled, hideValuePopover, onAfterChange]
  );

  useEffect(() => {
    if (hasValuePopoverChecker && isValuePopoverVisible) {
      document.addEventListener('scroll', hideValuePopover, true);

      return () => {
        document.removeEventListener('scroll', hideValuePopover, true);
      };
    }

    return () => {};
  }, [hasValuePopoverChecker, isValuePopoverVisible, hideValuePopover]);

  useEffect(() => {
    if (hasValuePopoverChecker && isValuePopoverVisible) {
      const handleOutsideClick = (event) => {
        if (elementRef.current && !elementRef.current.contains(event.target)) {
          hideValuePopover();
        }
      };
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('touchstart', handleOutsideClick);
      };
    }
    return () => {};
  }, [hasValuePopoverChecker, isValuePopoverVisible, hideValuePopover]);

  const renderIconType = () => {
    if (!!popoverProps && !selected) {
      return (
        <Popover
          mode={popoverMode}
          placement="top-left"
          title={popoverProps.title}
          description={popoverProps.content}
          appearance="dark"
          imgSrc={popoverProps.imgSrc}
          {...(isValuePopoverEnabled && {
            onHover: hideValuePopover,
          })}
        >
          <PopoverTrigger>
            <IconButton
              appearance="light-accent"
              glyph={iconGlyph}
              selected={selected}
              onClick={onClick}
              size="large"
            />
          </PopoverTrigger>
          <PopoverContent />
        </Popover>
      );
    }

    if (onClick) {
      return (
        <IconButton
          appearance="light-accent"
          glyph={iconGlyph}
          selected={selected}
          onClick={onClick}
          size="large"
        />
      );
    }

    return (
      <div className="ws-ui-slider-pattern__icon-wrapper">
        <Icon size={20} glyph={iconGlyph} />
      </div>
    );
  };

  return (
    <div
      ref={elementRef}
      className="ws-ui-slider-pattern__box"
      {...(hasValuePopoverChecker && {
        onMouseLeave: hideValuePopover,
      })}
    >
      <HorizontalStack gap="spacing-xs" verticalAlign="center">
        <div className="ws-ui-slider-pattern__content">
          {renderIconType()}
          {selected ? (
            <Box>
              <Paragraph>{title}</Paragraph>
            </Box>
          ) : (
            <Slider
              value={parsedValue}
              size="small"
              startPoint={startPoint}
              min={getMin(min, currentValue)}
              max={getMax(max, currentValue)}
              step={getStep(step, currentValue)}
              indeterminate={indeterminate}
              onChange={handleSliderChange}
              onAfterChange={onAfterChange}
              {...(isValuePopoverEnabled && {
                onFocus: () => {
                  toggleValuePopover(parsedValue);
                },
                onBlur: hideValuePopover,
              })}
            />
          )}
        </div>
        {selected ? (
          <Heading type="heading-6">{mixedLabel}</Heading>
        ) : (
          <div
            className="ws-ui-slider-pattern__value-wrapper"
            {...(!hasValuePopoverChecker && {
              onMouseEnter: showValuePopover,
              onMouseLeave: hideValuePopover,
            })}
          >
            {isValuePopoverEnabled && !indeterminate && (
              <ValuePopover
                mode={popoverMode}
                value={inputValue}
                isOpen={isValuePopoverVisible}
                {...valuePopoverProps}
              />
            )}

            {isValueEditable ? (
              <ValueInput
                value={inputValue}
                min={min}
                max={max}
                supportedUnits={supportedUnits}
                bold
                valueFormatter={roundValueFormatter}
                onChange={onChange}
                onBlur={handleValueInputBlur}
                {...(isValuePopoverEnabled && {
                  onInputChange: toggleValuePopover,
                })}
              />
            ) : (
              <Heading type="heading-6" singleLine>
                {inputValue}
              </Heading>
            )}
          </div>
        )}
      </HorizontalStack>
    </div>
  );
};

export default BasicSliderPattern;
